import Old1PublicBackLink from "../components/old1_PublicBackLink";
import Old1PublicSiteNav from "../components/old1_PublicSiteNav";
import Old1PublicSiteFooter from "../components/old1_PublicSiteFooter";
import PageSeo from "../../components/PageSeo";
import YoutubeArticleEmbed from "../../components/YoutubeArticleEmbed";
import { JOBUWIN_INTRO_VIDEO_ID, youtubeEmbedUrl, youtubeWatchUrl } from "../../lib/youtubeVideos";
import { buildPublicPageUrl } from "../../lib/pageSeo";

const PAGE_TITLE = "Introducing JOBUWIN: A More Human Way of Hiring Genuine People for Genuine Jobs";
const PAGE_PATH = "/old1/blog/introducing-jobuwin";
const PAGE_DESCRIPTION =
  "Why hiring lost its humanity — and how JOBUWIN helps genuine people and genuine companies find each other again through authentic communication.";

function old1_IntroducingJobuwinBlogPage({ currentUser, setCurrentUser }) {
  const videoJsonLd = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    embedUrl: youtubeEmbedUrl(JOBUWIN_INTRO_VIDEO_ID),
    contentUrl: youtubeWatchUrl(JOBUWIN_INTRO_VIDEO_ID),
    url: buildPublicPageUrl(PAGE_PATH),
    publisher: {
      "@type": "Organization",
      name: "JOBUWIN",
      url: buildPublicPageUrl("/"),
    },
  };

  return (
    <div className="home-page jw-public-page jw-public-home jw-public-blog-page">
      <PageSeo
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        path={PAGE_PATH}
        type="article"
        schemaType="article"
        jsonLd={videoJsonLd}
      />

      <Old1PublicSiteNav currentUser={currentUser} setCurrentUser={setCurrentUser} />

      <main id="main-content" className="jw-public-main">
        <section className="jw-public-article-section">
          <Old1PublicBackLink fallbackTo="/old1/blog" />

          <article className="jw-public-card jw-public-article">
            <span className="jw-public-eyebrow">Video</span>
            <h1>{PAGE_TITLE}</h1>

            <YoutubeArticleEmbed videoId={JOBUWIN_INTRO_VIDEO_ID} title={PAGE_TITLE} />

            <p>
              <strong>Hiring no longer feels human.</strong>
            </p>

            <p>
              Somewhere along the way, the process shifted from people trying to understand each other to systems trying to
              process volume. What was once a relatively organic process built around communication, experience, and
              judgement has slowly become a system dominated by optimisation, automation, filtering, and scale.
            </p>

            <p>
              Hiring managers are overwhelmed. Applicants are frustrated. Recruiters are under pressure. And increasingly,
              nobody feels connected to the process anymore.
            </p>

            <h2>A few years ago, hiring looked very different.</h2>

            <p>
              Companies would post a role and receive a manageable number of applications. Applicants would usually take the
              time to read the role properly, reflect on whether they were genuinely a fit, and apply thoughtfully. CVs
              represented real journeys built over years of experience, learning, mistakes, adaptation, and growth.
            </p>

            <p>
              There was a natural difference between candidates. Hiring managers could usually identify who had genuinely
              done the work, who had grown into the role over time, and who was likely to succeed based on their actual
              experience and communication.
            </p>

            <p>The process was never perfect, but overall, it worked reasonably well.</p>

            <h2>Today, the process feels completely different.</h2>

            <p>
              Hiring managers are flooded with hundreds of applications. Many look almost identical. Perfectly structured.
              Perfectly optimised. Perfectly aligned to the job description. The individuality that once existed between
              candidates is becoming increasingly difficult to identify.
            </p>

            <p>
              Not because every applicant suddenly became perfect, but because technology now allows applications to be
              generated, tailored, and optimised at scale.
            </p>

            <p>
              AI can now produce highly convincing CVs for almost any role within seconds. It can analyse job descriptions,
              mirror keywords, structure achievements, shape language, and present candidates in ways that often appear
              indistinguishable from genuine experience.
            </p>

            <p>
              As a result, hiring managers are no longer simply reviewing applications. They are trying to determine what is
              real and what is artificially manufactured.
            </p>

            <p>
              At the same time, applicants themselves are struggling with an entirely different set of problems. Fake jobs.
              Ghost listings. Automated rejection systems. Spam recruiters. Endless application portals. Processes that often
              feel cold, disconnected, and impossible to navigate.
            </p>

            <p>The result is a hiring ecosystem drowning in noise.</p>

            <p>
              And as more AI gets introduced to solve the growing problem, we are slowly entering a world where AI generates
              applications and more AI attempts to filter them. Automated systems optimise candidates for algorithms while
              other algorithms attempt to detect and rank those optimisations.
            </p>

            <p>Meanwhile, genuine people on both sides are becoming harder to find.</p>

            <p>
              <strong>That is the problem JOBUWIN was created to solve.</strong>
            </p>

            <h2>A more human way to hire</h2>

            <p>
              JOBUWIN is built around a very simple belief: hiring works best when real people can communicate genuinely with
              each other.
            </p>

            <p>
              Not through endless layers of optimisation, automation, keyword matching, and synthetic presentation, but
              through clearer human communication and more authentic interaction.
            </p>

            <p>
              The goal is not to remove technology. Technology is important and necessary. But technology should help humans
              connect better, not replace human understanding entirely.
            </p>

            <p>
              We believe hiring managers should be able to communicate who they really are, what they genuinely need, and
              what kind of person they are actually looking for beyond a static job description.
            </p>

            <p>
              We also believe applicants should have the opportunity to communicate beyond a polished CV. A real person is
              far more than a list of bullet points, keywords, and formatted achievements on a document.
            </p>

            <ul className="jw-public-article-list">
              <li>Thoughtfulness matters.</li>
              <li>Communication matters.</li>
              <li>Authenticity matters.</li>
              <li>Human connection matters.</li>
            </ul>

            <p>
              The hiring process should reward genuine alignment, honesty, curiosity, and real capability rather than simply
              rewarding whoever can optimise themselves best for an algorithm.
            </p>

            <p>That is why JOBUWIN is designed differently.</p>

            <p>
              The platform encourages more direct and authentic communication between hiring managers and applicants, helping
              genuine people and genuine jobs find each other again in an increasingly noisy and disconnected market.
            </p>

            <p>Because the hiring industry does not need more noise.</p>

            <p>It needs more trust. More clarity. More authenticity. More human connection.</p>

            <p>That is the journey JOBUWIN is beginning.</p>

            <p>
              Because genuine people still exist. And genuine companies still exist. The challenge now is helping them find
              each other again.
            </p>

            <p className="jw-public-article-cta">
              Watch the video above or visit{" "}
              <a href="https://www.jobuwin.com" rel="noopener noreferrer">
                www.jobuwin.com
              </a>
              .
            </p>
          </article>
        </section>
      </main>

      <Old1PublicSiteFooter />
    </div>
  );
}

export default old1_IntroducingJobuwinBlogPage;
